import * as Popover from "@radix-ui/react-popover";
import { AppWindow } from "lucide-react";
import React, { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDuration } from "../utils";
import { appIconUri } from "./processes/appIcon";
import { useHoverPin } from "./useHoverPin";

export function AppIcon({ name, compact = false }: { name: string; compact?: boolean }) {
  const icon = appIconUri(name);
  return icon ? (
    <img
      src={icon}
      alt=""
      className={`${compact ? "h-4 w-4" : "h-6 w-6"} shrink-0 object-contain`}
    />
  ) : (
    <AppWindow
      aria-hidden="true"
      className={`${compact ? "h-4 w-4" : "h-6 w-6"} shrink-0 text-text-mid`}
    />
  );
}

export default function AppUsagePreview({
  apps,
  appSeconds,
  label,
  compact = false,
}: { apps: string[]; appSeconds?: Record<string, number>; label?: string; compact?: boolean }) {
  const { t } = useTranslation();
  const items = apps.map(name => ({ name, seconds: appSeconds?.[name] }));
  const [highlighted, setHighlighted] = useState<string | null>(null);
  const anchor = useRef<HTMLFieldSetElement>(null);
  const popupId = useId();
  const hover = useHoverPin(500);
  const show = (name: string | null, delayed = false) => {
    setHighlighted(name);
    hover.show(delayed);
  };
  const trigger = (name: string | null, label: string, children: React.ReactNode) => (
    <button
      key={name ?? "more"}
      type="button"
      aria-label={label}
      aria-haspopup="dialog"
      aria-expanded={hover.open}
      aria-controls={hover.open ? popupId : undefined}
      onPointerEnter={() => show(name, true)}
      onPointerLeave={hover.leave}
      onFocus={() => show(name)}
      onBlur={hover.leave}
      onClick={() => {
        setHighlighted(name);
        hover.togglePin();
      }}
      className={`flex ${compact ? "h-6 min-w-5" : "h-8 min-w-6"} shrink-0 cursor-pointer items-center justify-center rounded-sm border-0 bg-transparent p-0 text-xs font-medium text-text-mid outline-none hover:text-info focus-visible:ring-2 focus-visible:ring-info/30`}
    >
      {children}
    </button>
  );
  return (
    <Popover.Root open={hover.open} onOpenChange={hover.onOpenChange}>
      <Popover.Anchor asChild>
        <fieldset
          ref={anchor}
          className="m-0 flex min-w-0 items-center gap-1.5 border-0 p-0"
          aria-label={label ?? t("process_directory.tools")}
          onPointerEnter={hover.keepOpen}
          onPointerLeave={hover.leave}
        >
          {items
            .slice(0, 3)
            .map(app =>
              trigger(
                app.name,
                `${app.name}: ${app.seconds === undefined ? t("process_directory.unavailable") : formatDuration(app.seconds)}`,
                <AppIcon name={app.name} compact={compact} />,
              ),
            )}
          {items.length > 3 &&
            trigger(
              null,
              t("activity.more_apps", { count: items.length - 3 }),
              `+${items.length - 3}`,
            )}
        </fieldset>
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          id={popupId}
          side="bottom"
          align="start"
          sideOffset={8}
          collisionPadding={12}
          aria-label={`${t("all_apps")} (${items.length})`}
          {...hover.contentProps}
          onInteractOutside={event => {
            // Every icon belongs to this popup; moving between them must not dismiss it.
            if (anchor.current?.contains(event.target as Node)) event.preventDefault();
          }}
          className="z-[120] max-h-72 w-max max-w-[calc(100vw-24px)] overflow-y-auto rounded-lg border border-outline bg-surface p-3 text-sm text-text shadow-lg"
        >
          <h3 className="mb-3 text-sm font-semibold">
            {t("all_apps")} ({items.length})
          </h3>
          <ul className="space-y-1">
            {items.map(app => (
              <li
                key={app.name}
                onPointerEnter={() => setHighlighted(app.name)}
                data-highlighted={highlighted === app.name ? "true" : undefined}
                className="flex items-center gap-3 rounded-md px-2 py-1 data-[highlighted=true]:bg-info/10"
              >
                <AppIcon name={app.name} />
                <span className="min-w-0 max-w-48 break-words">{app.name}</span>
                <span className="ml-auto pl-3 text-text-dim tabular-nums">
                  {app.seconds === undefined
                    ? t("process_directory.unavailable")
                    : formatDuration(app.seconds)}
                </span>
              </li>
            ))}
          </ul>
          <Popover.Arrow className="fill-surface" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
